<template>
  <div class="secret-reference-widget">
    <div class="secret-reference-widget__header">
      <div class="secret-reference-widget__title">
        <Icon name="lock-simple" :size="14" color="var(--vscode-charts-yellow, #ffd54f)" />
        <span>Secret Reference</span>
      </div>
      <span class="secret-reference-widget__vault">{{ effectiveVault }}</span>
    </div>

    <div class="secret-reference-widget__fields">
      <div class="secret-reference-widget__field">
        <label>Secret Ref</label>
        <InputBox
          :model-value="secretRef"
          placeholder="e.g. database/connection-string"
          :readonly="readonly"
          @update:model-value="updateField('ref', $event.toString())"
        />
      </div>

      <div class="secret-reference-widget__field">
        <label>Vault Alias</label>
        <SelectBox
          v-if="hasConfiguredVaults"
          :model-value="selectedVaultAlias"
          :options="vaultOptions"
          :disabled="readonly"
          @update:model-value="updateField('vault', $event.toString())"
        />
        <InputBox
          v-else
          :model-value="secretVault"
          placeholder="default"
          :readonly="readonly"
          @update:model-value="updateField('vault', $event.toString())"
        />
      </div>

      <div class="secret-reference-widget__field">
        <label>Version</label>
        <InputBox
          :model-value="secretVersion"
          placeholder="latest"
          :readonly="readonly"
          @update:model-value="updateField('version', $event.toString())"
        />
      </div>
    </div>

    <div v-if="resolvedVaultUri" class="secret-reference-widget__hint">
      Linked vault: {{ resolvedVaultUri }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/components/atoms/Icon.vue'
import InputBox from '@/components/atoms/InputBox.vue'
import SelectBox from '@/components/atoms/SelectBox.vue'
import type { SecretReferenceValue } from '@/types/configuration'
import { buildSecretReference } from '@/utils/secret-reference'

interface Props {
  modelValue: SecretReferenceValue
  readonly?: boolean
  vaultAliases?: string[]
}

interface Emits {
  (e: 'update:modelValue', value: SecretReferenceValue): void
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  vaultAliases: () => [],
})

const emit = defineEmits<Emits>()

const secretRef = computed(() => props.modelValue?.$secret?.ref || '')
const secretVault = computed(() => props.modelValue?.$secret?.vault || '')
const secretVersion = computed(() => props.modelValue?.$secret?.version || '')
const resolvedVaultUri = computed(() => props.modelValue?.$secret?._resolvedVaultUri || '')
const effectiveVault = computed(() => secretVault.value || 'default')
const hasConfiguredVaults = computed(() => props.vaultAliases.length > 0)
const defaultVaultAlias = computed(() => props.vaultAliases.find(alias => alias === 'default') || props.vaultAliases[0] || 'default')
const selectedVaultAlias = computed(() => secretVault.value || defaultVaultAlias.value)
const vaultOptions = computed(() => props.vaultAliases.map(alias => ({
  value: alias,
  label: alias === 'default' ? 'default (recommended)' : alias,
})))

function updateField(field: 'ref' | 'vault' | 'version', value: string) {
  const next = buildSecretReference({
    ref: field === 'ref' ? value : secretRef.value,
    vault: field === 'vault' ? value : secretVault.value,
    version: field === 'version' ? value : secretVersion.value,
  })

  emit('update:modelValue', next)
}
</script>

<style scoped lang="scss">
.secret-reference-widget {
  display: grid;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--vscode-widget-border, rgba(128, 128, 128, 0.35));
  border-radius: 8px;
  background: var(--vscode-editorWidget-background, rgba(127, 127, 127, 0.08));
}

.secret-reference-widget__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.secret-reference-widget__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
}

.secret-reference-widget__vault {
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(255, 213, 79, 0.14);
  color: var(--vscode-charts-yellow, #ffd54f);
  font-size: 11px;
  font-weight: 600;
}

.secret-reference-widget__fields {
  display: grid;
  gap: 12px;
}

.secret-reference-widget__field {
  display: grid;
  gap: 6px;

  label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--vscode-descriptionForeground, #8c8c8c);
  }
}

.secret-reference-widget__hint {
  font-size: 12px;
  color: var(--vscode-descriptionForeground, #8c8c8c);
  word-break: break-all;
}
</style>